<template>
	<main class="container xl:max-w-6xl">
		<div class="flex flex-col sm:flex-row pt-5 pb-12 sm:pt-8 sm:pb-16">
			<div class="lg:w-4/5 lg:pr-10">
				<section>
					<Breadcrumbs
						:crumbs="crumbsMaker('user')"
						class="sm:border-b sm:border-grey-light sm:pb-5 mb-2 sm:mb-7"
					/>
					<h1 class="text-left text-4xl sm:text-5xl tracking-wide mb-5">Karriere-Tipps</h1>
					<p v-if="!$isMobile.phone" class="text-lg max-w-3xl mb-3">
						Hier findest du Tipps für deine erfolgreiche Jobsuche und Bewerbung, sowie hilfreiche
						Artikel in Sachen Gehalt, Urlaub, Krankenstand und Kündigung - so bist du fit für den
						aktuellen Arbeitsmarkt und hast einen stressfreien Einstieg in deinen neuen Job!
					</p>
					<div class="flex items-center gap-x-2">
						<HokInput id="searchterm" v-model="articleSearchTerm" @enter="searchArticle">
							Nach Artikel suchen
						</HokInput>
						<HyButton @click="searchArticle">Suchen</HyButton>
					</div>
				</section>
				<section
					v-if="articlesBySearchTerm && articlesBySearchTerm.length"
					class="flex flex-col items-center"
				>
					<ItemList
						id="articles-search"
						:items="articlesBySearchTerm.slice(0, articlesShown)"
						:initial-size="9"
						:ssr="index => index < 3"
						type="Article"
						class="mb-6 sm:mb-8"
						@item-clicked="$trackWebsiteEvent('blog_click_result', {})"
					/>
					<HyButton
						v-if="articlesShown <= 3"
						variant="link"
						class="inline-block"
						:class="{ 'mb-8': $isMobile.any }"
						@click="articlesShown = 9"
						>Alle Ergebnisse anzeigen</HyButton
					>
				</section>
				<section v-if="$isMobile.phone" class="border border-grey-light p-5 mb-6 rounded-xl">
					<h3 class="text-2xl mb-5 leading-none">Kategorien</h3>
					<ul class="list-disc list-outside space-y-3 ml-4">
						<li
							v-for="category in magazineStore.getUserCategories"
							:key="category.name"
							@click.capture="e => toAnchor(e, category.url)"
						>
							<HokLink class="text-text hover:text-main-hover" :to="`#${category.url}`">{{
								category.name
							}}</HokLink>
						</li>
					</ul>
				</section>
				<section
					v-if="articleTeasers.bewerbung && articleTeasers.bewerbung.length"
					id="bewerbung"
					class="sm:pt-8 pb-12 border-grey-light border-b"
				>
					<h2 class="tracking-wide font-bold text-3xl sm:text-4xl mb-5">Bewerbung & Lebenslauf</h2>
					<div class="flex flex-col items-center">
						<ItemList
							id="articles-bewerbung"
							:items="articleTeasers.bewerbung"
							:ssr="index => index < 3"
							:start-at="0"
							:initial-size="3"
							:items-per-loading="9"
							type="Article"
							class="mb-6 sm:mb-8"
						/>
						<HyButton
							v-if="hasCoreArticle(articleTeasers.bewerbung)"
							class="self-start"
							:full-width="$isMobile.phone"
							size="lg"
							:to="articleTeasers['bewerbung'][0].articleUrl"
						>
							Mehr zum Thema Bewerbung<template v-if="!$isMobile.phone"> & Lebenslauf</template>
						</HyButton>
					</div>
				</section>
				<section
					v-if="articleTeasers.jobsuche && articleTeasers.jobsuche.length"
					id="jobsuche"
					class="pt-8 pb-12 border-grey-light border-b"
				>
					<h2 class="tracking-wide font-bold text-3xl sm:text-4xl mb-4">Alles zur Jobsuche</h2>
					<div class="flex flex-col items-center">
						<ItemList
							id="articles-jobsuche"
							:items="articleTeasers.jobsuche"
							:initial-size="3"
							type="Article"
							class="mb-6 sm:mb-8"
						/>
						<HyButton
							v-if="hasCoreArticle(articleTeasers.jobsuche)"
							class="self-start"
							:full-width="$isMobile.phone"
							size="lg"
							:to="articleTeasers.jobsuche[0].articleUrl"
						>
							Mehr zum Thema Jobsuche
						</HyButton>
					</div>
				</section>
				<section
					v-if="articleTeasers['ausbildung-lehre'] && articleTeasers['ausbildung-lehre'].length"
					id="ausbildung-lehre"
					class="pt-8 pb-12 border-grey-light border-b"
				>
					<h2 class="tracking-wide font-bold text-3xl sm:text-4xl mb-4">Ausbildung & Lehre</h2>
					<div class="flex flex-col items-center">
						<ItemList
							id="articles-ausbildung-lehre"
							:items="articleTeasers['ausbildung-lehre']"
							:initial-size="3"
							type="Article"
							class="mb-6 sm:mb-8"
						/>
						<HyButton
							v-if="hasCoreArticle(articleTeasers['ausbildung-lehre'])"
							class="self-start"
							:full-width="$isMobile.phone"
							size="lg"
							:to="articleTeasers['ausbildung-lehre'][0].articleUrl"
						>
							Mehr zum Thema Ausbildung<template v-if="!$isMobile.phone"> & Lehre</template>
						</HyButton>
					</div>
				</section>
				<section
					v-if="articleTeasers.arbeitsmarkt && articleTeasers.arbeitsmarkt.length"
					id="arbeitsmarkt"
					class="pt-8 pb-12 border-grey-light border-b"
				>
					<h2 class="tracking-wide font-bold text-3xl sm:text-4xl mb-4">
						Aktuelles zum Arbeitsmarkt
					</h2>
					<div class="flex flex-col items-center">
						<ItemList
							id="articles-arbeitsmarkt"
							:items="articleTeasers.arbeitsmarkt"
							:initial-size="3"
							type="Article"
							class="mb-6 sm:mb-8"
						/>
						<HyButton
							v-if="hasCoreArticle(articleTeasers.arbeitsmarkt)"
							class="self-start"
							:full-width="$isMobile.phone"
							size="lg"
							:to="articleTeasers['arbeitsmarkt'][0].articleUrl"
						>
							Mehr zum Thema Arbeitsmarkt
						</HyButton>
					</div>
				</section>
				<section
					v-if="articleTeasers.arbeitsrecht && articleTeasers.arbeitsrecht.length"
					id="arbeitsrecht"
					class="pt-8 pb-12 border-grey-light border-b"
				>
					<h2 class="tracking-wide font-bold text-3xl sm:text-4xl mb-4">
						Arbeitsalltag & Arbeitsrecht
					</h2>
					<div class="flex flex-col items-center">
						<ItemList
							id="articles-arbeitsrecht"
							:items="articleTeasers.arbeitsrecht"
							:initial-size="3"
							type="Article"
							class="mb-6 sm:mb-8"
						/>
						<HyButton
							v-if="hasCoreArticle(articleTeasers.arbeitsrecht)"
							class="self-start"
							:full-width="$isMobile.phone"
							size="lg"
							:to="articleTeasers['arbeitsrecht'][0].articleUrl"
						>
							Mehr zum Thema Arbeitsrecht
						</HyButton>
					</div>
				</section>
				<section
					v-if="articleTeasers['product-updates'] && articleTeasers['product-updates'].length"
					id="product-updates"
					class="pt-8"
				>
					<h2 class="tracking-wide font-bold text-3xl sm:text-4xl mb-4">Neuigkeiten von hokify</h2>
					<div class="flex flex-col items-center">
						<ItemList
							id="articles-product-updates"
							:items="articleTeasers['product-updates']"
							:initial-size="3"
							type="Article"
							class="mb-6 sm:mb-8"
						/>
						<HyButton
							v-if="hasCoreArticle(articleTeasers['product-updates'])"
							class="self-start"
							:full-width="$isMobile.phone"
							size="lg"
							:to="articleTeasers['product-updates'][0].articleUrl"
						>
							Mehr Neuigkeiten von hokify
						</HyButton>
					</div>
				</section>
			</div>
			<aside class="mt-14 hidden lg:block w-1/5">
				<div class="sticky top-20">
					<div
						v-if="magazineStore.getUserCategories && magazineStore.getUserCategories.length"
						class="mb-6 sm:mb-8"
					>
						<h3 class="text-3xl mb-6 leading-none">Kategorien</h3>
						<ul class="list-disc list-inside space-y-3 text-sm">
							<li
								v-for="category in magazineStore.getUserCategories"
								:key="category.name"
								@click.capture="e => toAnchor(e, category.url)"
							>
								<HokLink
									class="text-text hover:text-main-hover"
									:to="`/karriere/${category.url}`"
									>{{ category.name }}</HokLink
								>
							</li>
						</ul>
					</div>
					<CallToAction styling="user-box">
						<template #headline>Finde jetzt schneller deinen neuen Job!</template>
						<template #button><span class="text-main">Jetzt registrieren</span></template>
					</CallToAction>
				</div>
			</aside>
		</div>
		<div
			class="pt-8 border-grey-light border-t mb-4"
			:class="{ 'flex justify-between': !$isMobile.any }"
		>
			<div v-if="authors?.length" :class="$isMobile.any ? 'w-full mb-8 ' : 'w-3/5 mr-4'">
				<h3 class="font-bold mb-6 tracking-wide">
					Wer steckt hinter den den hokify Karriere-Tipps?
				</h3>
				<p>
					Unsere Autor:innen versogen euch mit aktuellen Blogs rund um die Themen Jobsuche,
					Bewerbung, Karriere & Arbeitsmarkt.
				</p>
				<div class="flex mb-4">
					<span
						v-for="author in authors"
						:key="author._id"
						class="flex flex-col items-center text-center mr-2"
					>
						<NuxtImg
							:src="(author.images && author.images.small) || '../../assets/img/unknown_user.png'"
							:alt="author.name"
							class="w-16 h-16 rounded-full mb-1 object-cover object-top"
						/>
						{{ author.displayName.split(' ').slice(0, -1).join(' ') }}
					</span>
				</div>
			</div>
			<div :class="$isMobile.any ? 'w-full' : 'w-2/5'">
				<FAQ :items="faqSection" styling="small" :title="''" />
			</div>
		</div>
		<SeoFooter
			:data="seoFooterData"
			:show-tab-view="false"
			:show-headline="false"
			alignment="left"
		/>

		<PopupHandler
			ref="popupHandler"
			:data-obj="loginBase.loginFormData"
			:mode="loginBase.loginBaseData.mode"
			@send-status="loginBase.handleSendStatus"
		/>
	</main>
</template>

<script lang="ts">
import { faqData } from '@hokify/shared-components-nuxt3/lib/data/short-faq';
import HokInput from '@hokify/shared-components-nuxt3/lib/components/HokInput.vue';
import type { IAPIOtherTerms } from '@hokify/common';
import type { IAPIArticleTeaser } from '@hokify/magazine-interface';
import { mapStores } from 'pinia';
import { getAllAlternateUrls, getCanonicalUrl, getSocialMediaMetaData } from '~/helpers/metadata';
import SeoFooter, { type ISeoFooterData } from '~/components/website/footer/SeoFooter.vue';
import Breadcrumbs from '~/components/website/Breadcrumbs.vue';
import { useMagazineBase } from '@/composables/MagazineBase';
import { crumbsMaker } from '@/helpers/magazine';
import PopupHandler from '@/components/website/PopupHandler.vue';
import { useLoginBase } from '@/composables/LoginBase';
import { useMagazineStore } from '@/stores/magazine';
import { useRootStore } from '@/stores/root';

export default defineNuxtComponent({
	name: 'KarrierePage',
	components: {
		HokInput,
		FAQ: defineAsyncComponent(
			() => import('@hokify/shared-components-nuxt3/lib/components/FAQ.vue')
		),
		SeoFooter,
		ItemList: defineAsyncComponent(() => import('~/components/website/list/ItemList.vue')),
		Breadcrumbs,
		CallToAction: defineAsyncComponent(
			() => import('@hokify/shared-components-nuxt3/lib/components/CallToAction.vue')
		),
		PopupHandler
	},
	setup() {
		definePageMeta({
			layout: 'default'
		});

		const rootStore = useRootStore();
		const { query } = useRoute();
		const path = `${rootStore.host}/karriere`;

		const canonical = getCanonicalUrl(path);
		const alternates = getAllAlternateUrls(path);
		const result: any = {
			title: 'hokify Karriere-Tipps: Vorlagen, Tipps und Tricks',
			link: [canonical, ...alternates],
			meta: [
				{ name: 'robots', content: 'index, follow' },
				{
					name: 'description',
					content:
						'Die hokify Karriere-Tipps liefern dir alle Infos für deine Jobsuche und berufliche Karriere. Alle Infos zu Urlaub, Kündigung, Krankenstand und mehr leicht erklärt und inklusive Vorlagen!'
				}
			]
		};

		result.meta.push(
			...getSocialMediaMetaData(result, rootStore.topLevelDomain, undefined, undefined, query)
		);

		useHead(result);
	},
	async asyncData({ $pinia }) {
		try {
			const { articleTeasers, loadArticles } = useMagazineBase($pinia);

			const result = await loadArticles({
				articleOverviewType: 'user',
				includeInterlinking: true
			});

			return {
				authors: result.authors,
				otherTerms: result.otherTerms,
				articleTeasers: articleTeasers.value
			};
		} catch (err: any) {
			throw createError(err ?? 'Error not defined');
		}
	},
	computed: {
		...mapStores(useMagazineStore, useRootStore),
		mappedSeoBranches():
			| {
					title: string;
					description: string;
					to: string;
					linkType?: 'visible' | 'invisible';
			  }[]
			| undefined {
			if (this.otherTerms?.interlinking?.jobField) {
				return this.otherTerms?.interlinking?.jobField.map(page => ({
					title: page.name,
					description: `${page.results}`,
					to: page.url,
					linkType: page.linkType
				}));
			}
			return undefined;
		},
		mappedSeoRegions():
			| {
					title: string;
					description: string;
					to: string;
					linkType?: 'visible' | 'invisible';
			  }[]
			| undefined {
			if (this.otherTerms?.interlinking?.locationHierarchy) {
				return this.otherTerms?.interlinking?.locationHierarchy.map(place => ({
					title: place.name,
					description: `${place.results}`,
					to: place.url,
					linkType: place.linkType
				}));
			}
			return undefined;
		},
		mappedSeoRecentJobSearches():
			| {
					title: string;
					to: string;
					linkType?: 'visible' | 'invisible';
			  }[]
			| undefined {
			if (this.otherTerms?.searches) {
				return this.otherTerms?.searches.map(search => ({
					title: search.name,
					to: search.url,
					linkType: search.linkType
				}));
			}
			return undefined;
		},
		seoFooterData(): ISeoFooterData[] {
			return [
				{
					title: 'Jobs nach Regionen',
					id: 'regions',
					searches: this.mappedSeoRegions || []
				},
				{
					title: 'Jobs nach Branchen',
					id: 'branches',
					searches: this.mappedSeoBranches || []
				},
				{
					title: 'Aktuelle Suchen',
					id: 'current-searches',
					searches: this.mappedSeoRecentJobSearches || []
				}
			];
		},
		popupHandler() {
			return this.$refs.popupHandler as any;
		}
	},
	data() {
		const otherTerms = undefined as IAPIOtherTerms | undefined;
		const articlesBySearchTerm = undefined as [] | undefined;
		const articleTeasers = {} as Record<string, IAPIArticleTeaser[]>;

		return {
			loginBase: useLoginBase(this.$nuxt),
			magazineBase: useMagazineBase(),
			articleTeasers,
			faqSection: faqData.faq,
			articleSearchTerm: '',
			articlesBySearchTerm,
			articlesShown: 3,
			otherTerms,
			crumbsMaker
		};
	},
	mounted() {
		this.loginBase.setPopupHandler(this.popupHandler);
	},
	methods: {
		hasCoreArticle(articles: IAPIArticleTeaser[]): boolean {
			return this.magazineBase.hasCoreArticle(articles);
		},
		toAnchor(e, anchor: string) {
			e.preventDefault();
			this.$scrollTo(`#${anchor}`, 300, {
				offset: this.$isMobile.any ? 0 : -56
			});
		},
		async searchArticle() {
			try {
				this.articlesBySearchTerm = await this.magazineStore.loadArticlesBySearchterm(
					this.articleSearchTerm
				);
				if (!this.articlesBySearchTerm?.length) {
					this.$snack.danger({
						text: 'Für diesen Suchbegriff haben wir keine Artikel gefunden - bitte passe deine Eingabe an.'
					});
				}
				this.articlesShown = 3;
				this.$trackWebsiteEvent('blog_trigger_search', {});
			} catch (err: any) {
				this.$nuxt.$errorHandler?.call(this, err);
				this.$snack.danger({
					text: 'Beim Suchen ist ein Fehler aufgetreten.'
				});
			}
		}
	}
});
</script>
